import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSync } from 'react-icons/fa';
import Button from 'common/components/button/Button';
import WizardFooterSlot from 'common/components/wizardFooterSlot/WizardFooterSlot';
import TablePagination from 'common/components/tablePagination/TablePagination';
import ExpressionSystem from 'common/components/expressionButton/ExpressionSystem';
import I18n from 'common/services/I18n';
import { PriceClassification, PriceFormulaPreview } from '../utils/types';
import mappingStyles from './StepMapping.module.scss';

export interface PricePreviewRowInfo {
    rowIndex: number;
    reference: string;
    name: string;
    priceText: string;
}

interface Props {
    rows: PricePreviewRowInfo[];
    previews: Map<number, PriceFormulaPreview>;
    overrides: Map<number, PriceClassification>;
    hasPriceColumn: boolean;
    analyzing: boolean;
    onReanalyze: () => void;
    onOverrideChange: (rowIndex: number, classification: PriceClassification | null) => void;
}

const StepPricePreview: React.FC<Props> = ({
    rows, previews, overrides, hasPriceColumn, analyzing, onReanalyze, onOverrideChange,
}) => {
    const { t } = useTranslation();

    const ROWS_PER_PAGE = 100;
    const [currentPage, setCurrentPage] = useState(0);
    const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
    const paginatedRows = rows.slice(currentPage * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE + ROWS_PER_PAGE);

    // Keep the page within bounds when the row set changes.
    useEffect(() => {
        if (currentPage > totalPages - 1) setCurrentPage(0);
    }, [currentPage, totalPages]);

    const effectiveOf = (rowIndex: number): PriceClassification =>
        overrides.get(rowIndex) ?? previews.get(rowIndex)?.classification ?? 'INHERIT';

    return (
        <div className={mappingStyles.container} style={{ padding: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <p style={{ margin: 0, color: '#666' }}>{t('elements.import_wizard.price_step_description')}</p>
                <Button variant="secondary" size="small" onClick={onReanalyze} disabled={analyzing || !hasPriceColumn}>
                    <FaSync style={{ marginRight: 6 }} />
                    {analyzing ? t('elements.import_wizard.price_analyzing') : t('elements.import_wizard.price_reanalyze')}
                </Button>
            </div>

            {!hasPriceColumn && (
                <p style={{ color: '#856404' }}>{t('elements.import_wizard.price_no_column')}</p>
            )}

            <div className={mappingStyles.tableWrapper}>
                <table className="table table-sm" style={{ width: '100%' }}>
                    <thead>
                        <tr className={mappingStyles.mappingRow}>
                            <th>{t('elements.import_wizard.field_reference')}</th>
                            <th>{t('elements.import_wizard.field_name')}</th>
                            <th>{t('elements.import_wizard.price_value_column')}</th>
                            <th>{t('elements.import_wizard.price_formula_column')}</th>
                            <th>{t('elements.import_wizard.price_override')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map(r => {
                            const preview = previews.get(r.rowIndex);
                            const detected = preview?.classification ?? 'INHERIT';
                            const effective = effectiveOf(r.rowIndex);
                            const hasTemplate = !!preview?.priceExpressionTemplate;
                            const displayFormula = preview?.displayFormula
                                ? ExpressionSystem.translateFormula(preview.displayFormula, I18n.language)
                                : null;
                            const priceNum = Number(r.priceText);
                            const hasPrice = r.priceText !== '' && isFinite(priceNum);
                            const locale = I18n.language || 'pt';
                            const priceShort = hasPrice ? `€ ${priceNum.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : r.priceText;
                            const priceFull = hasPrice ? `€ ${priceNum.toLocaleString(locale, { maximumFractionDigits: 20 })}` : r.priceText;
                            return (
                                <tr key={r.rowIndex}>
                                    <td>{r.reference}</td>
                                    <td>{r.name}</td>
                                    <td title={priceFull}>{priceShort}</td>
                                    <td>
                                        {displayFormula && (
                                            <div
                                                title={displayFormula}
                                                style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {displayFormula}
                                            </div>
                                        )}
                                    </td>
                                    <td>
                                        <select
                                            className={mappingStyles.extraSelect}
                                            value={effective}
                                            onChange={(e) => {
                                                const value = e.target.value as PriceClassification;
                                                // Store an override only when it differs from the detected value,
                                                // so re-analysing keeps following detection for untouched rows.
                                                onOverrideChange(r.rowIndex, value === detected ? null : value);
                                            }}
                                        >
                                            <option value="INHERIT">{t('elements.import_wizard.price_inherit')}</option>
                                            {hasTemplate && <option value="OWN">{t('elements.import_wizard.price_own')}</option>}
                                            <option value="FIXED">{t('elements.import_wizard.price_fixed')}</option>
                                        </select>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <WizardFooterSlot>
                    <TablePagination
                        page={currentPage}
                        totalPages={totalPages}
                        onChange={setCurrentPage}
                        previousLabel={t('elements.import_wizard.previous')}
                        nextLabel={t('elements.import_wizard.next')}
                        info={t('elements.import_wizard.page_info', { page: currentPage + 1, total: totalPages })}
                        firstTitle={t('elements.import_wizard.first_page')}
                        lastTitle={t('elements.import_wizard.last_page')}
                    />
                </WizardFooterSlot>
            )}
        </div>
    );
};

export default StepPricePreview;
